import { ApplicationRef, inject, Injectable, Injector } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DomPortalOutlet } from '@angular/cdk/portal';
import { OverlayZIndexService } from './overlay-z-index.service';

export interface DropdownPortalRef {
  outlet: DomPortalOutlet;
  container: HTMLElement;
}

/**
 * Provides a portal host anchored directly on `document.body` for rendering
 * dropdown overlays (date-picker calendar, select dropdown, etc.).
 *
 * Rendering at the body level ensures the dropdown completely escapes any
 * overflow, transform, or composited-layer constraints from ancestor elements.
 * On Safari iOS, scroll containers created by `overflow-y: auto` inside a
 * `position: fixed` dialog produce a composited rendering layer that traps
 * `position: fixed` children — they get clipped to the layer's bounds instead
 * of the viewport. Portaling to body sidesteps this entirely.
 */
@Injectable({ providedIn: 'root' })
export class DropdownPortalService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly overlayZIndex = inject(OverlayZIndexService);

  /**
   * Creates a new `DomPortalOutlet` whose host element is appended directly to
   * `document.body`. Each call returns an independent outlet; callers are
   * responsible for calling `outlet.dispose()` in `ngOnDestroy`.
   *
   * The container uses `position: fixed` so that `z-index` takes effect and
   * creates a proper stacking context. Its size is zero by default — it is
   * purely a mounting point for the portaled content.
   */
  createOutlet(): DropdownPortalRef {
    const container = this.document.createElement('div');
    container.style.position = 'fixed';
    this.document.body.appendChild(container);
    const outlet = new DomPortalOutlet(
      container,
      null,
      this.appRef,
      this.injector,
      this.document
    );
    return { outlet, container };
  }

  /**
   * Elevates the portal container's stacking context above the current top
   * dialog by obtaining the next z-index from `OverlayZIndexService`.
   * Must be called each time a dropdown is opened (not just on outlet
   * creation) so that dialogs opened after the outlet was first created
   * cannot obscure the dropdown.
   */
  bringToFront(container: HTMLElement): void {
    container.style.zIndex = String(this.overlayZIndex.next());
  }
}
